/**
 * <cart-drawer>
 *
 * Web Component that owns the cart drawer:
 *  - opens on `cart-drawer:open` event (dispatched by the open-cart button)
 *  - listens for `product:added` → re-renders cart from Section Rendering API
 *  - handles line-item quantity change, line removal
 *  - empty state recently viewed (sidecart only)
 *
 * Loaded ONLY from `sections/main-cart_drawer.liquid`. Does not live in global.js.
 */

import { Delegate } from '@theme/delegate';
import { DrawerComponent } from '@theme/drawer-component';
import { AddToCart } from '@theme/cart-api';
import { addFavorite } from '@theme/favorites-storage';
import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { disableBodyScroll } from '@theme/body-scroll-lock';
import {
  FAVORITES_RECENTLY_VIEWED_LIMIT,
  getRecentlyViewedHandles,
  RECENTLY_VIEWED_CHANGED_EVENT,
} from '@theme/recently-viewed';

const SIDECART_RECENTLY_VIEWED_LIMIT = FAVORITES_RECENTLY_VIEWED_LIMIT;
// Product attrs the analytics singleton reads from a list unit (data-product-id etc.).
const PRODUCT_DATASET_KEYS = ['productId', 'productTitle', 'variantId', 'price', 'productType'];

class CartDrawerComponent extends DrawerComponent {
  #options = {};
  #cartCount = null;
  #mobileCartCount = null;
  #recentlyViewedToken = 0;
  #recentlyViewedAbortController = null;
  #onRecentlyViewedChanged = null;

  #delegateElement = null;
  #renderToken = 0;
  #isUpdatingQuantity = false;

  #getRecentHandlesKey(handles = []) {
    return handles.join('|');
  }

  #getLocaleRoot() {
    return window.routes?.localeRootUrl || '';
  }

  #getCartSectionId() {
    return this.querySelector('#main-cartItems')?.dataset?.id || 'main-cart_drawer';
  }

  async #fetchCartSection(sectionId) {
    const response = await fetch(
      `${this.#getLocaleRoot()}/?section_id=${encodeURIComponent(sectionId)}&_=${Date.now()}`,
      {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
      }
    );

    if (!response.ok) throw new Error('Section render failed');

    return response.text();
  }

  #hasRecentRenderedContent(container, handlesKey) {
    return (
      container &&
      !container.hidden &&
      container.dataset.recentHandlesKey === handlesKey &&
      Boolean(container.querySelector('.cart-drawer__recently-viewed-swiper')) &&
      Boolean(container.querySelector('.cart-drawer__recent-hover-meta'))
    );
  }

  #enrichRecentSlide(slide) {
    const card = slide.querySelector('product-card') || slide.querySelector('.product-card');
    if (!card) return;

    const titleEl = slide.querySelector('.product-card__title');
    const priceEl = slide.querySelector('.product-card__price');

    slide.dataset.recentTitle = titleEl?.textContent?.trim() || '';
    slide.dataset.recentPrice = priceEl?.textContent?.trim().replace(/\s+/g, ' ') || '';
    slide.dataset.recentUrl = card.getAttribute('data-url') || card.dataset.url || '#';
    PRODUCT_DATASET_KEYS.forEach((key) => {
      slide.dataset[key] = card.dataset[key] || '';
    });
  }

  #createRecentHoverMeta() {
    const hoverMeta = document.createElement('div');
    hoverMeta.className = 'cart-drawer__recent-hover-meta';

    const hoverLink = document.createElement('a');
    hoverLink.className = 'cart-drawer__recent-hover-link';
    hoverLink.href = '#';
    hoverLink.setAttribute('data-list-item', '');

    const hoverTitle = document.createElement('span');
    hoverTitle.className = 'cart-drawer__recent-hover-title u-sh4';

    const hoverPrice = document.createElement('span');
    hoverPrice.className = 'cart-drawer__recent-hover-price u-sh4';

    hoverLink.append(hoverTitle, hoverPrice);
    hoverMeta.appendChild(hoverLink);

    return hoverMeta;
  }

  #bindRecentHoverMeta(swiperWrap, hoverMeta, container) {
    const hoverLink = hoverMeta.querySelector('.cart-drawer__recent-hover-link');
    const hoverTitle = hoverMeta.querySelector('.cart-drawer__recent-hover-title');
    const hoverPrice = hoverMeta.querySelector('.cart-drawer__recent-hover-price');

    if (!hoverLink || !hoverTitle || !hoverPrice) return;

    const showHover = (slide) => {
      hoverLink.href = slide.dataset.recentUrl || '#';
      hoverTitle.textContent = slide.dataset.recentTitle || '';
      hoverPrice.textContent = slide.dataset.recentPrice || '';
      PRODUCT_DATASET_KEYS.forEach((key) => {
        hoverLink.dataset[key] = slide.dataset[key] || '';
      });
      hoverLink.dataset.index = String(
        Array.prototype.indexOf.call(slide.parentElement?.children ?? [], slide)
      );
      container.classList.add('cart-drawer__recently-viewed--has-hover');
    };

    const hideHover = () => {
      container.classList.remove('cart-drawer__recently-viewed--has-hover');
    };

    swiperWrap.addEventListener('pointerover', (event) => {
      const slide = event.target.closest('[data-recently-viewed-card]');
      if (!slide || !swiperWrap.contains(slide)) return;
      showHover(slide);
    });

    swiperWrap.addEventListener('pointerout', (event) => {
      const related = event.relatedTarget;
      if (!related || !swiperWrap.contains(related)) hideHover();
    });

    swiperWrap.addEventListener('focusin', (event) => {
      const slide = event.target.closest('[data-recently-viewed-card]');
      if (!slide || !swiperWrap.contains(slide)) return;
      showHover(slide);
    });

    swiperWrap.addEventListener('focusout', (event) => {
      const related = event.relatedTarget;
      if (!related || !swiperWrap.contains(related)) hideHover();
    });
  }

  #updateRecentSwiper() {
    const swiperEl = this.querySelector('.cart-drawer__recently-viewed-swiper');
    if (!swiperEl?.swiper) return;

    requestAnimationFrame(() => {
      swiperEl.swiper?.updateSize?.();
      swiperEl.swiper?.update?.();
    });
  }

  connectedCallback() {
    super.connectedCallback();

    this.#options = this.#parseOptions();
    this.#cartCount = document.querySelector('.CartCount');
    this.#mobileCartCount = document.querySelector('[data-header-cart-count]');

    new AddToCart(this);

    document.addEventListener('cart-drawer:open', this.#onCartOpenEvent);
    document.addEventListener('product:added', this.#onProductAdded);
    document.addEventListener('cart:request-refresh', this.#onRequestRefresh);

    this.#onRecentlyViewedChanged = () => {
      if (this.#shouldShowRecentlyViewed()) {
        void this.#renderRecentlyViewed();
      }
    };
    document.addEventListener(RECENTLY_VIEWED_CHANGED_EVENT, this.#onRecentlyViewedChanged);

    this.#bindDelegatedEvents();
    void this.#renderRecentlyViewed();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart-drawer:open', this.#onCartOpenEvent);
    document.removeEventListener('product:added', this.#onProductAdded);
    document.removeEventListener('cart:request-refresh', this.#onRequestRefresh);
    document.removeEventListener(RECENTLY_VIEWED_CHANGED_EVENT, this.#onRecentlyViewedChanged);

    this.#recentlyViewedAbortController?.abort();
    this.#delegateElement?.off?.();
    this.#delegateElement = null;
  }

  #bindDelegatedEvents() {
    this.#delegateElement?.off?.();
    this.#delegateElement = new Delegate(this);

    this.#delegateElement.on(
      'click',
      '[data-action="update-item-quantity"], [data-action="remove-item"]',
      this.#updateItemQuantity
    );

    this.#delegateElement.on('click', '.cart-product-card__save', this.#onSaveForLater);
    this.#delegateElement.on('click', '[data-action="cart-checkout"]', this.#onCheckout);
  }

  #parseOptions() {
    try {
      return JSON.parse(this.getAttribute('data-settings')) ?? {};
    } catch {
      return {};
    }
  }

  #shouldShowRecentlyViewed() {
    const availableCount = this.#options.itemCount ?? 0;
    const soldOutCount = this.#options.soldOutCount ?? 0;

    return (
      this.dataset.showRecentlyViewed === 'true' &&
      availableCount === 0 &&
      soldOutCount === 0 &&
      Boolean(this.querySelector('[data-sidecart-recently-viewed]'))
    );
  }

  #onCartOpenEvent = () => {
    this.open();
  };

  // <cart-sold-out> traži re-render posle uklanjanja / save-for-later sold-out
  // kartice, da se count / footer / empty-state vrate u konzistentno stanje.
  #onRequestRefresh = () => {
    void this.#renderCart();
  };

  onOpened() {
    const cartHeader = this.querySelector('[data-drawer-header]');
    if (cartHeader) {
      document.documentElement.style.setProperty(
        '--cartHeader-height',
        `${cartHeader.offsetHeight}px`
      );
    }

    this.#updateRecentSwiper();
  }

  #onProductAdded = async () => {
    // Open immediately for instant feedback and let the render reconcile while the
    // drawer slides in — except on the very first item, where the drawer still
    // shows the empty state; there we render first so it doesn't flash empty.
    const wasEmpty = (this.#options.itemCount ?? 0) === 0;
    if (!wasEmpty) this.open();
    await this.#renderCart();
    if (wasEmpty) this.open();
  };

  #onSaveForLater = async (event, target) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isUpdatingQuantity) return;

    const button = target.closest('.cart-product-card__save');
    if (!button) return;

    const cartItem = button.closest('.cart-product-card');
    const lineId = button.getAttribute('data-line-id');
    if (!lineId) return;

    let options = {};
    try {
      const raw = button.getAttribute('data-options');
      if (raw) options = JSON.parse(raw);
    } catch {}

    addFavorite({
      productId: button.getAttribute('data-product-id'),
      variantId: button.getAttribute('data-variant-id'),
      handle: button.getAttribute('data-product-handle'),
      options,
      variantSelected: true,
    });

    const willEmptyCart =
      this.querySelectorAll('#cartData .cart-product-card').length <= 1;

    this.#isUpdatingQuantity = true;
    this.#setControlsBusy(true);

    if (!willEmptyCart) cartItem?.classList.add('removing');

    const token = ++this.#renderToken;

    try {
      const htmlPromise = this.#changeCartLine(lineId, 0);
      if (!willEmptyCart) {
        await new Promise((resolve) => setTimeout(resolve, 220));
      }
      const html = await htmlPromise;
      if (token !== this.#renderToken) return;

      if (html) {
        this.#replaceContent(html);
      } else {
        await this.#renderCart();
      }

      this.close();
      document.getElementById('favorites')?.open();
    } catch {
      cartItem?.classList.remove('removing');
      await this.#renderCart();
    } finally {
      this.#isUpdatingQuantity = false;
      this.#setControlsBusy(false);
    }
  };

  #getSoldOutLineIds() {
    return [
      ...this.querySelectorAll('.cart-drawer__sold-out .cart-product-card[data-line-id]'),
    ]
      .map((card) => card.getAttribute('data-line-id'))
      .filter(Boolean);
  }

  #onCheckout = async (event) => {
    event.preventDefault();

    const checkoutBtn = event.target.closest('[data-action="cart-checkout"]');
    if (!checkoutBtn || checkoutBtn.disabled) return;

    checkoutBtn.disabled = true;
    checkoutBtn.classList.add('is-loading');
    checkoutBtn.querySelector('.itemLoader')?.classList.add('is-visible');

    const localeRoot = window.routes?.localeRootUrl || '';
    const soldOutLineIds = this.#getSoldOutLineIds();

    // Best-effort čišćenje živih sold-out linija pre checkout-a. Persisted
    // (već izbačene) kartice imaju stale key → ignoriši grešku, ne prekidaj.
    for (const id of soldOutLineIds) {
      try {
        await fetch(`${localeRoot}/cart/change.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id, quantity: 0 }),
        });
      } catch {
        /* stale key / offline — nastavi */
      }
    }

    window.location.assign(`${localeRoot}/checkout`);
  };

  #setControlsBusy(busy) {
    this.classList.toggle('is-quantity-updating', busy);
  }

  #syncMinusButtonState(cartItem, quantity) {
    const minusBtn = cartItem?.querySelector('.cart-product-card__quantity-btn');
    if (!minusBtn || quantity > 1) return;

    minusBtn.classList.add('cart-product-card__quantity-btn--disabled');
    minusBtn.removeAttribute('data-action');
    minusBtn.removeAttribute('href');
  }

  #updateItemQuantity = async (event, target) => {
    event.preventDefault();
    event.stopPropagation();

    // Drop overlapping clicks: rapid +/- taps fire concurrent /cart/change.js
    // requests that race on the server and corrupt the optimistic UI. Ignore new
    // clicks while a change is in flight; controls are disabled until the re-render.
    if (this.#isUpdatingQuantity) return;
    this.#isUpdatingQuantity = true;
    this.#setControlsBusy(true);

    const cartItem = target.closest('.cart-product-card');
    const quantity = parseInt(target.getAttribute('data-quantity'), 10);
    const lineId = target.getAttribute('data-line-id');

    // Removing the last remaining line empties the cart — skip the slide-out so
    // the empty state (text, button, recently viewed) appears instantly instead
    // of after the exit animation.
    const willEmptyCart =
      quantity === 0 && this.querySelectorAll('.cart-product-card').length <= 1;

    // Optimistic, loader-free update: reflect the change instantly and reconcile
    // once the request returns. Removing → slide the card out; quantity → bump the
    // number right away.
    if (quantity === 0) {
      if (!willEmptyCart) cartItem?.classList.add('removing');
    } else {
      const valueEl = cartItem?.querySelector('.cart-product-card__quantity-value');
      if (valueEl) valueEl.textContent = String(quantity);
      this.#syncMinusButtonState(cartItem, quantity);
    }

    // Guard against out-of-order responses from rapid clicks: only the latest
    // action gets to swap the drawer in (mirrors #renderCart's token).
    const token = ++this.#renderToken;

    try {
      const htmlPromise = this.#changeCartLine(lineId, quantity);
      // Let the slide-out finish before the body is swapped underneath it.
      if (quantity === 0 && !willEmptyCart) {
        await new Promise((resolve) => setTimeout(resolve, 220));
      }
      const html = await htmlPromise;
      if (token !== this.#renderToken) return;
      if (html) {
        this.#replaceContent(html);
      } else {
        await this.#renderCart();
      }
    } catch {
      cartItem?.classList.remove('removing');
      await this.#renderCart();
    } finally {
      this.#isUpdatingQuantity = false;
      this.#setControlsBusy(false);
    }
  };

  // Change a line and get the re-rendered drawer in ONE request (Section Rendering
  // via the cart AJAX `sections` param) — no second fetch, no loading state.
  async #changeCartLine(lineId, quantity) {
    const sectionId = this.#getCartSectionId();
    const response = await fetch(`${this.#getLocaleRoot()}/cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: lineId, quantity, sections: sectionId }),
    });

    if (!response.ok) throw new Error('Cart update failed');

    const data = await response.json();
    return data?.sections?.[sectionId] ?? null;
  }

  async #renderCart() {
    const token = ++this.#renderToken;

    try {
      const html = await this.#fetchCartSection(this.#getCartSectionId());
      if (token !== this.#renderToken) return;

      this.#replaceContent(html);
    } catch {
      /* Keep the current drawer contents on failure. */
    }
  }

  // Defensive: the empty state (text, button) and recently-viewed must appear
  // instantly. A stale/CDN-cached section render can still carry the reveal hooks
  // (`animatedContent` + `data-animation`) that RevolutionAnimation picks up via
  // its document.body MutationObserver. Strip them before the fragment enters the
  // DOM so nothing gets observed/animated.
  #stripAnimationHooks(root) {
    root
      ?.querySelectorAll('#main-cartItems, [data-sidecart-recently-viewed]')
      .forEach((el) => {
        el.classList.remove('animatedContent');
        el.removeAttribute('data-animation');
        el.removeAttribute('data-loaded');
      });
  }

  #replaceContent(html) {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
    this.#stripAnimationHooks(tempElement);

    let tempOptions = {};
    try {
      tempOptions = JSON.parse(
        tempElement.querySelector('#sidecart')?.getAttribute('data-settings') ?? '{}'
      );
    } catch {}

    const number = tempOptions.itemCount ?? 0;
    const totalCount = tempOptions.totalItemCount ?? number;
    const soldOutCount = tempOptions.soldOutCount ?? 0;
    this.#options = { ...this.#options, itemCount: number, totalItemCount: totalCount, soldOutCount };

    this.#updateHeaderCount(number, totalCount, soldOutCount);

    if (this.#cartCount) {
      this.#cartCount.textContent = Number(totalCount) > 0 ? `(${totalCount})` : '';
      this.#cartCount.hidden = Number(totalCount) <= 0;
    }
    if (this.#mobileCartCount) {
      this.#mobileCartCount.textContent = String(totalCount);
      this.#mobileCartCount.hidden = Number(totalCount) <= 0;
    }
    window.theme = window.theme || {};
    window.theme.cartCount = parseInt(totalCount, 10);

    this.#replaceDrawerShell(tempElement);
    this.#bindDelegatedEvents();

    document
      .querySelectorAll('.cart-product-card__loader.is-visible')
      .forEach((loader) => loader.classList.remove('is-visible'));

    document.dispatchEvent(new CustomEvent('cart:refreshed'));

    setTimeout(() => {
      const focus = this.querySelector('[data-scrollable]');
      // Re-register the lock only while the drawer is actually open —
      // cart:request-refresh also re-renders a closed drawer (BFCache restore),
      // and locking then leaves the page stuck with body overflow:hidden.
      if (focus && this.isOpen) {
        disableBodyScroll(focus, bodyScrollLockOptions);
      }
      void this.#renderRecentlyViewed();
    }, 200);
  }

  #updateHeaderCount(availableCount, totalCount, soldOutCount) {
    const countEl = this.querySelector('[data-cart-drawer-count]');
    if (!countEl) return;

    const available = Number(availableCount) || 0;
    const soldOut = Number(soldOutCount) || 0;
    const total = Number(totalCount) || 0;
    const displayCount = available > 0 ? available : soldOut > 0 ? total : 0;

    countEl.textContent = `(${displayCount})`;
    countEl.hidden = displayCount <= 0;
  }

  #replaceDrawerShell(tempElement) {
    const source = tempElement.querySelector('#sidecart');
    if (!source) return;

    const sourceBody = source.querySelector('[data-drawer-body]');
    const currentBody = this.querySelector('[data-drawer-body]');
    const scrollTop = currentBody?.scrollTop ?? 0;

    if (sourceBody && currentBody) {
      currentBody.replaceWith(sourceBody.cloneNode(true));
    }

    this.#replaceFooter(
      source.querySelector('[data-drawer-footer]'),
      this.querySelector('[data-drawer-footer]')
    );

    this.refreshScrollableElement();

    const newBody = this.querySelector('[data-drawer-body]');
    if (newBody) newBody.scrollTop = scrollTop;
  }

  // Totals/discounts change on quantity updates, but the checkout button does not.
  // Replacing the whole footer re-mounts the button and can reset its style (section
  // settings mismatch on AJAX re-render, hover animation state, etc.).
  #replaceFooter(sourceFooter, currentFooter) {
    if (!sourceFooter) {
      currentFooter?.remove();
      return;
    }

    if (!currentFooter) {
      this.appendChild(sourceFooter.cloneNode(true));
      return;
    }

    const sourceSummary = sourceFooter.querySelector('.cart-drawer__summary');
    const currentSummary = currentFooter.querySelector('.cart-drawer__summary');
    const existingCheckout = currentFooter.querySelector('[data-action="cart-checkout"]');

    if (sourceSummary && currentSummary) {
      currentSummary.replaceWith(sourceSummary.cloneNode(true));
    } else if (sourceSummary) {
      currentFooter.prepend(sourceSummary.cloneNode(true));
    }

    if (!existingCheckout) {
      const sourceCheckout = sourceFooter.querySelector('[data-action="cart-checkout"]');
      if (sourceCheckout) {
        currentFooter.appendChild(sourceCheckout.cloneNode(true));
      }
    }
  }

  #getRecentSwiperSettings(count) {
    const settingsEl = this.querySelector('[data-sidecart-recent-swiper-settings]');
    if (!settingsEl) return null;

    try {
      const settingsByCount = JSON.parse(settingsEl.textContent.trim());
      return settingsByCount[String(count)] ?? null;
    } catch {
      return null;
    }
  }

  async #fetchRecentCard(handle, signal) {
    const localeRoot = window.routes?.localeRootUrl || '';
    // -card-flat = same card resting on the packshot (last product image), per the side cart
    // Figma note. The PDP recently-viewed section fetches plain `-card` and keeps the model
    // shot; two sections rather than a query param, since Liquid cannot read one here.
    const url = `${localeRoot}/products/${encodeURIComponent(handle)}?section_id=helper-recently-viewed-card-flat`;

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) return null;

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = doc.querySelector('[data-recently-viewed-card]');
      if (!card) return null;

      card.classList.add('swiper-slide');
      return card;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return null;
    }
  }

  async #renderRecentlyViewed() {
    const container = this.querySelector('[data-sidecart-recently-viewed]');
    this.#stripAnimationHooks(this);
    if (!this.#shouldShowRecentlyViewed() || !container) {
      this.#recentlyViewedAbortController?.abort();
      if (container) {
        container.hidden = true;
        container.replaceChildren();
        delete container.dataset.recentHandlesKey;
      }
      return;
    }

    const token = ++this.#recentlyViewedToken;
    this.#recentlyViewedAbortController?.abort();
    this.#recentlyViewedAbortController = new AbortController();
    const { signal } = this.#recentlyViewedAbortController;

    const handles = getRecentlyViewedHandles(SIDECART_RECENTLY_VIEWED_LIMIT).filter(Boolean);
    const handlesKey = this.#getRecentHandlesKey(handles);

    if (!handles.length) {
      container.hidden = true;
      container.replaceChildren();
      delete container.dataset.recentHandlesKey;
      return;
    }

    if (this.#hasRecentRenderedContent(container, handlesKey)) return;

    try {
      const slides = (
        await Promise.all(handles.map((handle) => this.#fetchRecentCard(handle, signal)))
      ).filter(Boolean);

      if (signal.aborted || token !== this.#recentlyViewedToken) return;

      if (!slides.length) {
        container.hidden = true;
        container.replaceChildren();
        delete container.dataset.recentHandlesKey;
        return;
      }

      const count = Math.min(slides.length, SIDECART_RECENTLY_VIEWED_LIMIT);
      const swiperSettings = this.#getRecentSwiperSettings(count);

      if (signal.aborted || token !== this.#recentlyViewedToken || !swiperSettings) return;

      container.className = `cart-drawer__recently-viewed cart-drawer__recently-viewed--count-${count}`;
      container.replaceChildren();

      const title = document.createElement('h3');
      title.className = 'cart-drawer__recently-viewed-title u-sh4';
      title.textContent = this.dataset.recentlyViewedTitle ?? '';

      const swiper = document.createElement('swiper-init');
      swiper.className = 'swiper cart-drawer__recently-viewed-swiper';
      swiper.setAttribute('data-swiper-settings', JSON.stringify(swiperSettings));

      // Attach before the element connects: Swiper mounts synchronously on
      // insertion and fires swiper:initialized then, so a listener added afterwards
      // would miss it (and #updateRecentSwiper would never re-measure).
      swiper.addEventListener(
        'swiper:initialized',
        () => {
          requestAnimationFrame(() => {
            this.#updateRecentSwiper();
          });
        },
        { once: true }
      );

      const wrapper = document.createElement('div');
      wrapper.className = 'swiper-wrapper';
      slides.slice(0, SIDECART_RECENTLY_VIEWED_LIMIT).forEach((slide) => {
        this.#enrichRecentSlide(slide);
        wrapper.appendChild(slide);
      });
      swiper.appendChild(wrapper);

      const swiperWrap = document.createElement('div');
      swiperWrap.className = 'cart-drawer__recently-viewed-swiper-wrap';
      swiperWrap.appendChild(swiper);

      const hoverMeta = this.#createRecentHoverMeta();

      // Reveal the container BEFORE inserting the swiper so Swiper mounts against
      // the real width. Mounting inside a still-hidden (display:none) container
      // makes it measure 0 and stretch every slide to the full container height —
      // the visible flash until a later update() corrects it.
      container.hidden = false;
      container.dataset.recentHandlesKey = handlesKey;

      container.append(title, swiperWrap, hoverMeta);
      this.#bindRecentHoverMeta(swiperWrap, hoverMeta, container);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      container.hidden = true;
      container.replaceChildren();
      delete container.dataset.recentHandlesKey;
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawerComponent);
}

// bfcache restore (back from /checkout) resurrects the page with the checkout
// button still disabled + spinning — reset it so it is usable again.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('[data-action="cart-checkout"]').forEach((btn) => {
    btn.disabled = false;
    btn.classList.remove('is-loading');
    btn.querySelector('.itemLoader')?.classList.remove('is-visible');
  });
});
